import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import './Auth.css';
import { safeRedirectPath } from '../../App';

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  // Alert setup step retired 2026-10-12 (hourly/daily emails sunset): land on the redirect target or the Notches Dashboard.
  const setupPath = safeRedirectPath(redirectParam) || '/notches/dashboard';
  const { verifyEmail, user, token } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendTimer, setResendTimer] = useState(60);

  useEffect(() => {
    if (user?.emailVerified) {
      navigate(setupPath);
    }
  }, [user, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendTimer]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        nextInput?.focus();
      }

      // Auto-submit when all fields are filled
      if (newCode.every((digit) => digit !== '') && index === 5) {
        handleVerify(newCode.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await verifyEmail(codeToVerify);
      navigate(setupPath);
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
      setCode(['', '', '', '', '', '']);
      document.getElementById('code-0')?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!token) return;

    setResendDisabled(true);
    setResendTimer(60);
    setError(null);

    try {
      await authService.resendVerificationCode(token);
      alert('Verification code sent successfully! Please check your email.');
    } catch (err: any) {
      setError('Failed to resend code. Please try again.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="verification-icon">✉️</div>
        <h2>Verify Your Email</h2>
        <p className="subtitle">
          We've sent a 6-digit verification code to
          <br />
          <strong>{user?.email}</strong>
        </p>

        {error && <div className="error-alert">{error}</div>}

        <div className="verification-code-container">
          {code.map((digit, index) => (
            <input
              key={index}
              id={`code-${index}`}
              type="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="code-input"
              autoFocus={index === 0}
            />
          ))}
        </div>

        <button
          onClick={() => handleVerify()}
          className="btn-primary"
          disabled={isLoading || code.some((d) => !d)}
        >
          {isLoading ? 'Verifying...' : 'Verify Email'}
        </button>

        <div className="resend-section">
          <p>Didn't receive the code?</p>
          <button
            onClick={handleResend}
            className="btn-link"
            disabled={resendDisabled}
          >
            {resendDisabled
              ? `Resend in ${resendTimer}s`
              : 'Resend Verification Code'}
          </button>
        </div>
      </div>
    </div>
  );
};